import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ChangeDetectionStrategy,
  ViewChild,
  Inject
} from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, map, tap, take } from 'rxjs/operators';

import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { get } from 'lodash';
import { DialogService } from '../../../ui/dialog/dialog.service';
import { DatasService, GetSpheresFromProfileIdGQL } from '@polylearn/models';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'apps/landing/src/environments/environment';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { DialogData } from '../../../ui/dialog/dialog.interface';
import { UtilitiesService } from '../../../shared/utilities.service';

@Component({
  selector: 'polylearn-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UsersListComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  @Input() search: string;

  displayedColumns: string[];
  @Input() dataSource;
  filteredDataSource$;

  @Input() role: 'manager' | 'teacher';
  @Input() type: 'student' | 'teacher';

  @Output() actionClick = new EventEmitter<{ type: string; data: any }>();

  actions;

  search$ = new BehaviorSubject<string>(this.search);
  updateDataSource$ = new BehaviorSubject<void>(null);

  constructor(
    private dialogService: DialogService,
    private dataService: DatasService,
    private getSpheresFromProfileIdGQL: GetSpheresFromProfileIdGQL,
    private matSnackBar: MatSnackBar,
    public dialog: MatDialog,
    private utilitiesService: UtilitiesService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    //Add '${implements OnChanges}' to the class.
    if (changes['search']) {
      this.search$.next(this.search);
    }

    if (changes['dataSource']) {
      this.updateDataSource$.next();
    }
  }

  ngOnInit(): void {
    this.displayedColumns =
      this.type === 'student'
        ? [
            'first_name',
            'last_name',
            'email',
            'objective',
            'estimated_scores',
            'manual_estimated_scores',
            'time_spent',
            'level',
            'expiration_date',
            'actions'
          ]
        : [
            'first_name',
            'last_name',
            'email',
            'is_manager',
            'time_spent',
            'level',
            'expiration_date',
            'actions'
          ];

    if (environment.EXAM === 'delf') {
      this.displayedColumns = this.displayedColumns.filter(
        col => col !== 'manual_estimated_scores'
      );
    }

    if (!(environment as any)?.levels?.options?.length) {
      this.displayedColumns = this.displayedColumns.filter(
        col => col !== 'level'
      );
    }

    this.filteredDataSource$ = combineLatest(
      this.search$,
      this.updateDataSource$
    )
      .pipe(debounceTime(200))
      .pipe(
        map(([search, update]) => {
          if (!search || search?.length === 0) {
            return this.dataSource;
          }
          return this.dataSource.filter(
            data =>
              (data?.user?.first_name as string)
                ?.toLocaleLowerCase()
                .includes(search.toLocaleLowerCase()) ||
              (data?.user?.last_name as string)
                ?.toLocaleLowerCase()
                .includes(search.toLocaleLowerCase())
          );
        }),
        map(dataSource => {
          let x = new MatTableDataSource<any>(dataSource);
          x.paginator = this.paginator;
          x.sortingDataAccessor = (item, property) => {
            if (property === 'displayname') {
              return `${item?.user?.first_name} ${item?.user?.last_name}`;
            }

            if (property.includes('.')) {
              return get(item, property);
            }
            return item[property];
          };
          x.sort = this.sort;
          return x;
        })
      );

    this.actions = [
      {
        roles: ['manager', 'teacher'],
        types: ['student'],
        icon: 'visibility',
        text: 'see_profile',
        click: (student, index) => {
          console.log(student);
          this.actionClick.emit({
            type: 'show_student',
            data: {
              student
            }
          });
        }
      },
      {
        roles: ['manager', 'teacher'],
        types: ['student'],
        icon: 'groups',
        text: 'see_groups',
        click: async (student, index) => {
          console.log(student);

          let spheresNames = (
            await this.getSpheresFromProfileIdGQL
              .fetch(
                {
                  profileId: student.id
                },
                {
                  context: {
                    headers: {
                      role: 'teacher'
                    }
                  }
                }
              )
              .pipe(take(1))
              .toPromise()
          ).data.profile_sphere_association.map(s =>
            s.sphere.is_global && s.sphere.teacher_id
              ? `${s.sphere.name} (restreint)`
              : s.sphere.name
          );

          let text = spheresNames?.length
            ? spheresNames.join('<br>')
            : 'Pas de groupe';

          this.dialogService.alert(
            {
              title: `Groupes de ${student?.user?.first_name}`
              /*
            icon: {
              name: 'groups'
            },
            */
            },
            {
              text
            }
          );
        }
      },
      {
        roles: ['manager', 'teacher'],
        types: ['student'],
        icon: 'get_app',
        text: 'download_pdf_profile',
        click: (student, index) => {
          console.log(student);
          this.actionClick.emit({
            type: 'download_pdf',
            data: {
              studentId: student.id
            }
          });
        }
      },
      {
        roles: ['manager'],
        types: ['student', 'teacher'],
        text: 'send_back_login',
        icon: 'email',
        click: (student, index) => {
          this.actionClick.emit({
            type: 'resend_login',
            data: {
              email: student.user.email
            }
          });
        }
      },
      {
        roles: ['manager'],
        types: ['student', 'teacher'],
        text: 'extend_account',
        icon: 'replay',
        click: (student, index) => {
          this.actionClick.emit({
            type: 'extend',
            data: {
              student
            }
          });
        }
      },
      {
        roles: ['manager'],
        types: ['student', 'teacher'],
        text: 'delete_account',
        icon: 'delete',
        click: (student, index) => {
          this.actionClick.emit({
            type: 'delete_account',
            data: {
              student,
              index
            }
          });
        }
      }
    ]
      .filter(
        action =>
          action.roles.includes(this.role) && action.types.includes(this.type)
      )
      .map(r => {
        return {
          ...r,
          text: `users_list.actions.${r.text}`
        };
      });

    if ((environment as any)?.levels?.options?.length) {
      this.actions = [
        ...this.actions,
        {
          roles: ['manager'],
          types: ['student', 'teacher'],
          text: 'users_list.actions.switch_level',
          icon: 'swap_horiz',
          click: (student, index) => {
            this.utilitiesService
              .getSwitchLevelDialog(student.id, student.level)
              .subscribe(res => {
                if (res) {
                  this.actionClick.emit({
                    type: 'update',
                    data: null
                  });
                }
              });
          }
        }
      ].filter(
        action =>
          action.roles.includes(this.role) && action.types.includes(this.type)
      );
    }
  }

  public getUserActions(user) {
    if (this.role === 'manager') {
      if (user.is_admin) {
        return [];
      }

      let managerAction;

      if (user.is_manager) {
        managerAction = {
          roles: ['manager'],
          types: ['teacher'],
          text: 'users_list.actions.remove_from_manager',
          icon: 'toggle_off',
          click: (student, index) => {
            console.log('student', student);
            this.dialogService
              .confirm(
                {
                  title: `Suppression des droits administrateur à ${student.user.email}`
                },
                {
                  text: `Êtes-vous sur de vouloir retirer ${student.user.email} des administrateurs ?`
                }
              )
              .subscribe(res => {
                if (res) {
                  this.dataService
                    .updateData$(
                      'profiles',
                      'manager',
                      student.id,
                      {
                        is_manager: false
                      },
                      'id'
                    )
                    .subscribe(() => {
                      this.matSnackBar.open(
                        `${student.user.email} est supprimer des administrateurs`,
                        null,
                        {
                          duration: 6000
                        }
                      );
                      this.actionClick.emit({
                        type: 'update',
                        data: null
                      });
                    });
                }
              });
          }
        };
      } else {
        managerAction = {
          roles: ['manager'],
          types: ['teacher'],
          text: 'users_list.actions.add_to_manager',
          icon: 'toggle_on',
          click: (student, index) => {
            console.log('student', student);
            this.dialogService
              .confirm(
                {
                  title: `Ajout des droits administrateur à ${student.user.email}`
                },
                {
                  text: `Êtes-vous sur de vouloir passer ${student.user.email} administrateur ?`
                }
              )
              .subscribe(res => {
                if (res) {
                  this.dataService
                    .updateData$(
                      'profiles',
                      'manager',
                      student.id,
                      {
                        is_manager: true
                      },
                      'id'
                    )
                    .subscribe(() => {
                      this.matSnackBar.open(
                        `${student.user.email} est maintenant administrateur`,
                        null,
                        {
                          duration: 6000
                        }
                      );
                      this.actionClick.emit({
                        type: 'update',
                        data: null
                      });
                    });
                }
              });
          }
        };
      }

      return [managerAction].filter(
        action =>
          action.roles.includes(this.role) && action.types.includes(this.type)
      );
    }

    return [];
  }

  public identify(index, item) {
    return item.id;
  }
}
